"use client";

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ToolId, toolIdToSlug } from './utils/slugs';
import { AdUnit } from './AdUnit';
import {
    HomeIcon, AboutIcon, SimpleCalcIcon, ConverterIcon, ManagementIcon, MyKitchenIcon, ResourcesIcon,
    ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon, CakePricingIcon, RecipeScalerIcon, PercentIcon, MeasurementIcon,
    BakingPanIcon, YeastIcon, ThermometerIcon, DocumentIcon, ClipboardIcon,
    CalendarIcon, SparklesIcon,
    BirthdayCakeIcon,
    SearchIcon,
    PaletteIcon,
    WaterDropIcon,
    BoxIcon,
    RecipeCostIcon,
    PastryBagIcon,
    FlavorWheelIcon,
    MacaronIcon,
    ToolboxIcon,
    XIcon,
    ClockIcon,
    ShoppingCartIcon,
    IngredientSwapIcon
} from './Icons';

export const navConfig = [
    {
        id: 'popular',
        title: '🔥 Popular Tools',
        icon: <SparklesIcon className="w-6 h-6" />,
        tools: [
            { id: 'cake-pricing', title: 'Cake Pricing', icon: <CakePricingIcon className="w-5 h-5" /> },
            { id: 'recipe-scaler', title: 'Recipe Scaler', icon: <RecipeScalerIcon className="w-5 h-5" /> },
            { id: 'bakery-order-planner', title: 'Order Planner', icon: <CalendarIcon className="w-5 h-5" /> },
        ]
    },
    {
        id: 'business',
        title: '📊 Business Tools',
        icon: <ManagementIcon className="w-6 h-6" />,
        tools: [
            { id: 'cost-per-recipe', title: 'Cost Calculator', icon: <RecipeCostIcon className="w-5 h-5" /> },
            { id: 'inventory-management', title: 'Inventory Tracker', icon: <BoxIcon className="w-5 h-5" /> },
            { id: 'order-management', title: 'Order Manager', icon: <ClipboardIcon className="w-5 h-5" /> },
        ]
    },
    {
        id: 'converters',
        title: '🔧 Converters',
        icon: <ConverterIcon className="w-6 h-6" />,
        tools: [
            { id: 'measurement-converter', title: 'Measurement', icon: <MeasurementIcon className="w-5 h-5" /> },
            { id: 'temperature-converter', title: 'Temperature', icon: <ThermometerIcon className="w-5 h-5" /> },
            { id: 'baking-pan-converter', title: 'Pan Size', icon: <BakingPanIcon className="w-5 h-5" /> },
        ]
    }
];

interface NavItemProps {
    icon: React.ReactNode;
    children: React.ReactNode;
    href: string;
    onClick?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, children, href, onClick }) => {
    const pathname = usePathname();
    const active = pathname === href;

    return (
        <li>
            <Link href={href} onClick={onClick} className={`flex items-center w-full text-left gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${active ? 'bg-brand-pink text-white shadow-md shadow-brand-pink/20 font-bold' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'}`}>
                <span className={`transition-colors ${active ? 'text-white' : 'text-stone-400 group-hover:text-stone-600'}`}>{icon}</span>
                <span className="">{children}</span>
            </Link>
        </li>
    );
};

interface AccordionCategoryProps {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    isOpen: boolean;
    setIsOpen: () => void;
}

const AccordionCategory: React.FC<AccordionCategoryProps> = ({ title, icon, children, isOpen, setIsOpen }) => (
    <div>
        <button 
            onClick={setIsOpen} 
            className={`flex items-center justify-between w-full text-left gap-3 px-3 py-2.5 rounded-lg transition-colors group ${isOpen ? 'text-stone-900' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'}`}
            aria-expanded={isOpen}
        >
            <div className="flex items-center gap-3">
                <span className={`${isOpen ? 'text-brand-pink' : 'text-stone-400 group-hover:text-stone-600 transition-colors'}`}>{icon}</span>
                <span className="font-semibold text-sm">{title}</span>
            </div>
            {isOpen ? <ChevronUpIcon className="w-4 h-4 text-brand-pink" /> : <ChevronDownIcon className="w-4 h-4 text-stone-400 group-hover:text-stone-600" />}
        </button>
        {isOpen && (
            <ul className="pl-4 py-1 space-y-0.5 mt-1 border-l border-stone-100 ml-6">
                {children}
            </ul>
        )}
    </div>
);

interface ToolLinkProps {
    children: React.ReactNode;
    icon: React.ReactNode;
    href: string;
    onClick?: () => void;
}

const ToolLink: React.FC<ToolLinkProps> = ({ children, icon, href, onClick }) => {
    const pathname = usePathname();
    const active = pathname === href;

    return (
        <li>
            <Link href={href} onClick={onClick} className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-md text-xs transition-all duration-200 ${active ? 'bg-brand-pink/10 text-brand-pink font-bold border-l-2 border-brand-pink' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'}`}>
                <span className={`w-4 h-4 ${active ? 'text-brand-pink' : 'text-stone-400'}`}>{icon}</span>
                <span className="">{children}</span>
            </Link>
        </li>
    );
};

interface SidebarProps {
    onToggle?: () => void;
    onNavigate?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ onToggle, onNavigate }) => {
    const [openCategory, setOpenCategory] = useState<string | null>('core');
    const [searchTerm, setSearchTerm] = useState('');

    const toggleCategory = (id: string) => {
        setOpenCategory(prevOpenCategory => (prevOpenCategory === id ? null : id));
    };

    const isSearching = searchTerm.trim().length > 0;
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    const filteredNavConfig = useMemo(() => {
        if (!isSearching) return navConfig;
        return navConfig.map(category => {
            const filteredTools = category.tools.filter(tool => (tool.title || '').toLowerCase().includes(lowerCaseSearchTerm));
            if (category.title.toLowerCase().includes(lowerCaseSearchTerm)) return category;
            if (filteredTools.length > 0) return { ...category, tools: filteredTools };
            return null;
        }).filter((category): category is NonNullable<typeof category> => category !== null);
    }, [lowerCaseSearchTerm, isSearching]);

    return (
        <aside className="h-full bg-white text-stone-900 flex flex-col overflow-hidden">
            <div className="h-4"></div>
            <div className="px-6 mb-6 flex items-center gap-2">
                <div className="relative group flex-1">
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                        type="text"
                        placeholder="Filter..."
                        aria-label="Filter tools"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full bg-stone-50 border border-transparent focus:border-brand-pink/50 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none transition-all shadow-sm"
                    />
                </div>
                {onToggle && (
                    <button 
                        onClick={onToggle} 
                        className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-stone-100 text-stone-500"
                        aria-label="Close sidebar"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                )}
            </div>

            <nav className="flex-1 overflow-y-auto px-4 pb-8 scrollbar-thin scrollbar-thumb-stone-200">
                <ul className="space-y-1">
                    <li className="mb-4 lg:hidden border-b border-stone-100 pb-4">
                        <ul className="space-y-1 px-2">
                            <NavItem href="/recipes" icon={<DocumentIcon className="w-5 h-5"/>} onClick={onNavigate}>Recipes</NavItem>
                            <NavItem href="/blog" icon={<HomeIcon className="w-5 h-5"/>} onClick={onNavigate}>Blog</NavItem>
                            <NavItem href="/about" icon={<AboutIcon className="w-5 h-5"/>} onClick={onNavigate}>About</NavItem>
                            <li>
                                <div className="pt-3 px-1">
                                    <Link href="/saas" onClick={onNavigate} className="flex items-center justify-center w-full px-4 py-3 text-sm font-bold text-white bg-brand-pink rounded-xl shadow-md hover:bg-brand-pink/90 transition-all">
                                        Bakery Pro
                                    </Link>
                                </div>
                            </li>
                        </ul>
                    </li>
                    {filteredNavConfig.map(category => (
                        <li key={category.id} className="mb-1">
                            <AccordionCategory title={category.title} icon={category.icon} isOpen={isSearching || openCategory === category.id} setIsOpen={() => toggleCategory(category.id)}>
                                {category.tools.map(tool => (
                                    <ToolLink key={tool.id} href={toolIdToSlug(tool.id as ToolId)} icon={tool.icon} onClick={onNavigate}>{tool.title}</ToolLink>
                                ))}
                            </AccordionCategory>
                        </li>
                    ))}
                    <li className="mt-4 px-3">
                        <Link href="/tools/all-tools" className="flex items-center justify-between w-full px-4 py-2 text-xs font-bold text-brand-pink bg-brand-pink/5 rounded-lg hover:bg-brand-pink/10 transition-colors">
                            View All Tools <ChevronRightIcon className="w-4 h-4" />
                        </Link>
                    </li>
                </ul>
            </nav>

            <div className="p-6 bg-white border-t border-stone-50">
                <p className="text-[10px] text-stone-300 text-center uppercase tracking-widest">© 2026 Sweety's Bakery</p>
            </div>
        </aside>
    );
};